import api from "@/api";
import Vue from "vue";
import deepClone from "@/utils/deep-clone";

import dateDiff from "@/utils/date-diff";

const state = {
    list: [],
    details: {},
};

const getters = {
    list(state) {
        return deepClone(
            state.list.map(item => {
                return {
                    ...item,
                    percentage: calculateProgressPercentage(item.progressSteps),
                    progressSteps: item.progressSteps.map(step => ({
                        ...step,
                        deadlineDays: Math.round(dateDiff.days(step.deadline, Date.now()) + 1),
                    })),
                    lastCertificateId:
                        item.certificates && item.certificates.length
                            ? item.certificates[item.certificates.length - 1]
                            : null,
                };
            }),
        );
    },
    details(state) {
        return id => {
            if (state.details[id]) {
                const progress = state.details[id];

                return deepClone({
                    ...progress,
                    percentage: calculateProgressPercentage(progress.progressSteps),
                    progressSteps: progress.progressSteps.map(step => ({
                        ...step,
                        deadlineDays: Math.round(dateDiff.days(step.deadline, Date.now()) + 1),
                    })),
                });
            } else {
                return deepClone(state.list.find(p => p._id === id));
            }
        };
    },
    element(state) {
        return ({ progressId, stepId }) => {
            const progress = state.list.find(progress => progress._id === progressId);
            const step = progress?.progressSteps?.find(step => step._id === stepId);

            if (step) {
                return deepClone({
                    ...(step.learningElement || {}),
                    optional: step.optional,
                    unlocked: step.unlocked,
                    completed: step.completed,
                    progress,
                });
            }
            return null;
        };
    },
    next(state) {
        return ({ progressId, stepId }) => {
            const progress = state.list.find(progress => progress._id === progressId);
            if (!progress) {
                return;
            }

            const currentStepIndex = progress.progressSteps?.findIndex(step => step._id === stepId);
            if (currentStepIndex == null || currentStepIndex === -1) {
                return;
            }

            if (currentStepIndex === progress.progressSteps.length - 1) {
                return { completed: true, percentage: 100 };
            }

            return deepClone({
                id: progress.progressSteps[currentStepIndex + 1]._id,
                percentage: calculateProgressPercentage(progress.progressSteps, {
                    includeCurrentStep: true,
                }),
            });
        };
    },
    todo(state) {
        return deepClone(
            getters
                .list(state)

                // Filter out completed paths
                .filter(progress => !progress.completed)

                // Extract the steps
                .map(progress =>
                    progress.progressSteps.map(step => ({
                        ...step,
                        learningPath: progress.learningPath,
                        progressId: progress._id,
                        percentage: progress.percentage,
                    })),
                )
                .flat()

                // Select steps that are not completed, nor skipped, and unlocked
                .filter(step => !step.completed && !step.skipped && step.unlocked)

                // Only select the first step of each path, as in some case we can end up with
                // multiple steps for the same path (i.e. optional steps unlock the next step,
                // so you can end up with more than one entry per path)
                .filter(
                    (step, index, steps) =>
                        index === steps.findIndex(s => s.progressId === step.progressId),
                )

                // Sort by deadline
                .sort((a, b) => {
                    if (a.deadlineDays < b.deadlineDays) {
                        return -1;
                    }
                    if (a.deadlineDays > b.deadlineDays) {
                        return 1;
                    }
                    return 0;
                }),
        );
    },
};

const actions = {
    getList({ commit }) {
        return api.get("/api/learning/progress").then(res => {
            const list = res.data && res.data.items ? res.data.items : [];
            commit("updateList", { list });
        });
    },
    get({ commit }, { payload: { id } }) {
        return api.get(`/api/learning/progress/${id}`).then(res => {
            commit("updateDetails", { progress: res.data });
        });
    },
    openStep(_, { payload: { progressId, stepId } }) {
        return api.post(`/api/learning/progress/${progressId}/step/${stepId}/open`);
    },
    skipStep(_, { payload: { progressId, stepId } }) {
        return api.post(`/api/learning/progress/${progressId}/step/${stepId}/skip`);
    },
    completeStep(_, { payload: { progressId, stepId, body } }) {
        return api.post(`/api/learning/progress/${progressId}/step/${stepId}/completed`, body);
    },
    create(_, { payload: { pathCommonId, userIds = [], teamIds = [] } }) {
        return Promise.all(
            userIds
                .map(userId =>
                    api.post(`/api/learning/progress`, {
                        userId,
                        pathCommonId,
                    }),
                )
                .concat(
                    teamIds.map(teamId =>
                        api.post(`/api/learning/progress`, {
                            teamId,
                            pathCommonId,
                        }),
                    ),
                ),
        );
    },
    selfenrol({ commit }, { payload: { pathCommonId } }) {
        return api
            .post(`/api/learning/progress/selfenrol`, {
                pathCommonId: pathCommonId,
            })
            .then(res => {
                commit("updateDetails", { progress: res.data });
                return res.data;
            });
    },
    delete(_, { payload: { id } }) {
        return api.delete(`/api/learning/progress/${id}`);
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateDetails(state, { progress }) {
        Vue.set(state.details, progress._id, progress);
    },
};

/**
 * Calculate the progress in percentage
 *
 * @param {Array} steps
 *
 * @return {Number} percentage
 */
function calculateProgressPercentage(steps = [], opts = {}) {
    const stepsCount = steps.length;

    let stepsCompleted = steps.filter(step => Boolean(step.completed || step.skipped)).length;
    if (opts.includeCurrentStep) {
        stepsCompleted += 1;
    }

    return stepsCount ? Math.min(100, Math.round((stepsCompleted / stepsCount) * 100)) : 0;
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export const __test__calculateProgressPercentage = calculateProgressPercentage;
